import { FunctionComponent, memo } from "react";
import * as Yup from "yup";
import { Field, Form, Formik } from "formik";
import { Button, FieldError } from "@edgetier/components";
import SelectLanguage from "components-for/filters/select-language";
import { faSave } from "@fortawesome/pro-solid-svg-icons";
import { getTimeToRead, trimObjectValues } from "@edgetier/utilities";
import { useQueryClient } from "react-query";
import { axios } from "utilities/axios";
import toast from "react-hot-toast";
import Axios from "axios";
import { getServerError } from "utilities/get-server-error";
import { IProps, ISpellingExemptionFormValues } from "./exemption-form.types";
import { ISpellingExemptionGetResponse, Route } from "@edgetier/watchtower-types";
import { updateSpellingExemptionCache } from "./exemption-form.utilities";
import "./exemption-form.scss";

/**
 * Form used to create or edit a spelling exemption
 * @param props.spellingExemption   Optional spelling exemption
 * @param props.writingIssue        Writting issue used to pre-populate the form
 * @param props.toggleAction        Callback method called after submit
 */
const ExemptionForm: FunctionComponent<IProps> = ({ spellingExemption, toggleAction, writingIssue }) => {
    const queryClient = useQueryClient();
    const formInitialValues =
        typeof spellingExemption === "undefined"
            ? { exemption: writingIssue ?? "", languageIsoCode: null }
            : spellingExemption;

    const preSubmit = async (exemptionValues: ISpellingExemptionFormValues) => {
        const trimmedValues = trimObjectValues(exemptionValues);
        try {
            if (typeof spellingExemption !== "undefined") {
                const { spellingExemptionId } = spellingExemption;
                await axios.patch(
                    Route.SpellingExemption.replace(":spellingExemptionId", spellingExemptionId),
                    trimmedValues
                );
                queryClient.setQueriesData<ISpellingExemptionGetResponse>(
                    Route.SpellingExemptions,
                    updateSpellingExemptionCache({
                        spellingExemptionId: spellingExemption.spellingExemptionId,
                        ...exemptionValues,
                    })
                );
            } else {
                await axios.post<ISpellingExemptionGetResponse>(Route.SpellingExemptions, trimmedValues);
                queryClient.invalidateQueries(Route.SpellingExemptions);
            }

            const successMessage =
                typeof spellingExemption !== "undefined"
                    ? `Successfully updated ${trimmedValues.exemption}`
                    : `Successfully created ${trimmedValues.exemption}`;
            toast.success(successMessage, { duration: getTimeToRead(successMessage, 1000) });
        } catch (error) {
            if (Axios.isAxiosError(error)) {
                toast.error(getServerError(error));
            } else {
                const errorMessage =
                    typeof spellingExemption !== "undefined"
                        ? "Failed to edit an exemption"
                        : "Failed to create an exemption";
                toast.error(errorMessage, { duration: getTimeToRead(errorMessage, 1000) });
            }
        }
        toggleAction();
    };

    return (
        <Formik<ISpellingExemptionFormValues>
            initialValues={formInitialValues}
            validationSchema={Yup.object().shape({
                exemption: Yup.string().trim().required("Required"),
                languageIsoCode: Yup.string().nullable(),
            })}
            onSubmit={preSubmit}
        >
            {({ isSubmitting, isValid }) => (
                <Form autoComplete="off" name="exemption-form">
                    <div className="field">
                        <FieldError name="exemption" />
                        <label htmlFor="exemption">Exemption</label>
                        <Field id="exemption" name="exemption" type="text" placeholder="Exemption" />
                    </div>
                    <div className="field">
                        <FieldError name="languageIsoCode" />
                        <label htmlFor="languageIsoCode">Language</label>
                        <SelectLanguage isSingleSelect name="languageIsoCode" />
                    </div>
                    <Button type="submit" icon={faSave} isLoading={isSubmitting} disabled={!isValid || isSubmitting}>
                        Submit
                    </Button>
                </Form>
            )}
        </Formik>
    );
};

export default memo(ExemptionForm);
